import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, X, FileText, Trophy } from "lucide-react";
import { adminApi } from "../../lib/api";
import RichTextEditor from "./RichTextEditor";
import { toast } from "sonner";

const SECTIONS = [
  { key: "client", label: "The Client" },
  { key: "challenge", label: "The Challenge" },
  { key: "solution", label: "The Solution" },
  { key: "difference", label: "The Difference" },
  { key: "outcome", label: "The Outcome" },
  { key: "closing", label: "Closing summary" },
];

const empty = { name: "", kind: "blog", description: "", body: "", sections: {}, order: 0 };

export default function ContentTemplatesManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => adminApi.contentTemplates.list().then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...empty, order: items.length + 1 });
  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await adminApi.contentTemplates.remove(id);
    toast.success("Template deleted");
    load();
  };

  if (editing) return <Editor item={editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;

  const blog = items.filter((t) => t.kind === "blog");
  const cases = items.filter((t) => t.kind === "case_study");

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display font-extrabold text-2xl text-navy">Content Templates</h1>
        <button data-testid="template-add-btn" onClick={startNew} className="inline-flex items-center gap-1.5 bg-orange hover:bg-orange-dark text-white font-bold text-sm px-5 py-2.5 rounded-full transition-colors"><Plus size={16} /> New template</button>
      </div>
      <p className="text-slate-500 text-sm mb-8">Reusable starter skeletons. They appear as "Start from a template" in the Blog editor and "Fill with template" in Success Stories, so new content stays fast and on-brand.</p>

      {loading ? <p className="text-sm text-slate-400">Loading…</p> : (
        <div className="space-y-8">
          <Group title="Blog templates" icon={FileText} list={blog} onEdit={setEditing} onRemove={remove} />
          <Group title="Success story templates" icon={Trophy} list={cases} onEdit={setEditing} onRemove={remove} />
        </div>
      )}
    </div>
  );
}

function Group({ title, icon: Icon, list, onEdit, onRemove }) {
  return (
    <div>
      <h2 className="font-display font-bold text-navy mb-3 inline-flex items-center gap-2"><Icon size={16} className="text-orange" /> {title}</h2>
      {list.length === 0 ? <p className="text-sm text-slate-400">No templates yet.</p> : (
        <div className="space-y-3">
          {list.map((t) => (
            <div key={t.id} data-testid={`template-row-${t.id}`} className="flex items-center gap-4 bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-navy truncate">{t.name || "Untitled"}</p>
                {t.description && <p className="text-xs text-slate-400 mt-0.5 truncate">{t.description}</p>}
              </div>
              <button onClick={() => onEdit(t)} className="text-sm font-semibold text-orange hover:text-orange-dark px-3 py-1.5">Edit</button>
              <button data-testid={`template-delete-${t.id}`} onClick={() => onRemove(t.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Editor({ item, onDone, onCancel }) {
  const [form, setForm] = useState({ ...empty, ...item, sections: { ...(item.sections || {}) } });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSection = (key, v) => setForm((f) => ({ ...f, sections: { ...f.sections, [key]: v } }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give the template a name"); return; }
    setSaving(true);
    try {
      if (item.id) await adminApi.contentTemplates.update(item.id, form);
      else await adminApi.contentTemplates.create(form);
      toast.success("Template saved");
      onDone();
    } catch { toast.error("Could not save"); setSaving(false); }
  };

  return (
    <div>
      <button onClick={onCancel} className="text-sm font-semibold text-slate-500 hover:text-navy mb-4 inline-flex items-center gap-1"><X size={15} /> Back</button>
      <h1 className="font-display font-extrabold text-2xl text-navy mb-6">{item.id ? "Edit template" : "New template"}</h1>

      <div className="max-w-2xl space-y-5">
        <div>
          <label className="block text-sm font-semibold text-navy mb-1.5">Template name</label>
          <input data-testid="template-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="e.g. How-to / Guide article" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-navy mb-1.5">Short description (shown in the picker)</label>
          <input data-testid="template-description" value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Used for</label>
            <select data-testid="template-kind" value={form.kind} onChange={(e) => set("kind", e.target.value)} className={inputCls}>
              <option value="blog">Blog post</option>
              <option value="case_study">Success story</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Order</label>
            <input data-testid="template-order" type="number" value={form.order} onChange={(e) => set("order", parseInt(e.target.value) || 0)} className={inputCls} />
          </div>
        </div>

        {form.kind === "blog" ? (
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Template content</label>
            <p className="text-xs text-slate-400 mb-2">Use placeholders in [square brackets] as prompts the writer can fill in.</p>
            <RichTextEditor testId="template-body" value={form.body || ""} onChange={(v) => set("body", v)} />
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-slate-400">Fill in the sections you want this template to pre-populate. Leave a section blank to skip it.</p>
            {SECTIONS.map((s) => (
              <div key={s.key} className="border-t border-slate-100 pt-4 first:border-t-0 first:pt-0">
                <label className="block text-sm font-semibold text-navy mb-1.5">{s.label}</label>
                <RichTextEditor testId={`template-section-${s.key}`} value={form.sections?.[s.key] ?? ""} onChange={(v) => setSection(s.key, v)} />
              </div>
            ))}
          </div>
        )}

        <button data-testid="template-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-60 text-white font-bold px-6 py-2.5 rounded-full transition-colors">
          <Save size={16} /> {saving ? "Saving…" : "Save template"}
        </button>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40";
